import {
	AlertToast,
	ErrorToast,
	SuccessToast,
	WarningToast,
} from "@/shared/notifications/AlertToast";
import {
	getAlertTheme,
	withAlertTheme,
} from "@/shared/notifications/alertTheme";
import Swal from "sweetalert2";
import type { SweetAlertOptions } from "sweetalert2";

type AlertRequestPayload = {
	Name?: string | null;
	[key: string]: unknown;
};

type Callback = (request: AlertRequestPayload) => void | Promise<void>;

function getDisplayName(
	request: AlertRequestPayload,
	name: string | null,
): string {
	if (name) {
		return name;
	}

	if (typeof request.Name === "string" && request.Name.trim()) {
		return request.Name;
	}

	return "this item";
}

function resolveNoticeContent(
	title: string,
	text: string,
	defaultTitle: string,
	defaultText: string,
): {
	title: string;
	description?: string;
} {
	const isDefaultNotice = title === defaultTitle && text === defaultText;

	if (isDefaultNotice) {
		return {
			title,
			description: text,
		};
	}

	if (title !== defaultTitle) {
		return {
			title,
			description: text,
		};
	}

	const compactTitle = text.trim();
	if (compactTitle.length <= 72) {
		return {
			title: compactTitle || defaultTitle,
		};
	}

	return {
		title: defaultTitle,
		description: text,
	};
}

export class AlertRequest {
	public static success = (
		text = "Your changes have been saved successfully.",
		title = "Saved",
	) => {
		const notice = resolveNoticeContent(
			title,
			text,
			"Saved",
			"Your changes have been saved successfully.",
		);
		void SuccessToast(notice.title, notice.description);
	};

	public static failed = (
		text = "Your changes could not be saved. Please try again or contact support.",
		title = "Couldn't save",
	) => {
		const notice = resolveNoticeContent(
			title,
			text,
			"Couldn't save",
			"Your changes could not be saved. Please try again or contact support.",
		);
		void ErrorToast(notice.title, notice.description);
	};

	public static warning = (text: string, title = "Warning") => {
		const notice = resolveNoticeContent(title, text, "Warning", text);
		void WarningToast(notice.title, notice.description);
	};

	public static confirm = (
		callback: Callback,
		request: AlertRequestPayload,
		name: string | null = null,
		showSuccessToast = true,
	) => {
		const theme = getAlertTheme();
		const displayName = getDisplayName(request, name);
		const options = withAlertTheme(
			{
				title: "Delete this item?",
				text: `${displayName} will be permanently removed. This cannot be undone.`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete",
				cancelButtonText: "Cancel",
			},
			theme,
		) as unknown as SweetAlertOptions;

		void Swal.fire(options)
			.then(async (result) => {
				if (result.isConfirmed) {
					try {
						await callback(request);
						if (showSuccessToast) {
							await AlertToast.success();
						}
					} catch (error) {
						console.error(error);
						await AlertToast.failed();
					}
				}
			})
			.catch(() => {
				void AlertToast.failed();
			});
	};

	public static confirmAsync = async (
		callback: Callback,
		request: AlertRequestPayload,
		name: string | null = null,
	) => {
		const theme = getAlertTheme();
		const displayName = getDisplayName(request, name);
		const options = withAlertTheme(
			{
				title: "Delete this item?",
				text: `${displayName} will be permanently removed. This cannot be undone.`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete",
				cancelButtonText: "Cancel",
			},
			theme,
		) as unknown as SweetAlertOptions;
		const alert = await Swal.fire(options);

		if (alert.isConfirmed) {
			await callback(request);
			await SuccessToast("Deleted", `${displayName} has been removed.`);
		}
	};

	public static genericConfirmation = <T, TResult = void>(
		callback: (request: T) => TResult | Promise<TResult>,
		request: T,
	) => {
		const theme = getAlertTheme();
		const options = withAlertTheme(
			{
				title: "Confirm this action?",
				text: "Are you sure you want to proceed?",
				icon: "question",
				showCancelButton: true,
				confirmButtonColor: theme.confirmColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Yes, continue",
				cancelButtonText: "Cancel",
			},
			theme,
		) as unknown as SweetAlertOptions;

		void Swal.fire(options).then(async (result) => {
			if (result.isConfirmed) {
				try {
					await callback(request);
					await SuccessToast("Done", "Your action was completed.");
				} catch (error) {
					console.error(error);
					void ErrorToast(
						"Couldn't complete action",
						"Please try again or contact support.",
					);
				}
			}
		});
	};

	public static delete = () => {
		const theme = getAlertTheme();
		const options = withAlertTheme(
			{
				title: "Delete this item?",
				text: "This action cannot be undone.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete",
				cancelButtonText: "Cancel",
			},
			theme,
		) as unknown as SweetAlertOptions;
		return Swal.fire(options);
	};

	public static deleteConfirmed = (text: string) => {
		const notice = resolveNoticeContent("Deleted", text, "Deleted", text);
		void SuccessToast(notice.title, notice.description);
	};
}
